import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

/**
 * Esta conta é anunciante da Vitrine? E ela tem imóveis?
 *
 * As duas perguntas moram juntas porque servem à MESMA decisão: qual painel
 * abrir. Separá-las em dois hooks faria a tela decidir com uma resposta e
 * esperar a outra, e o painel piscaria — o defeito que `/planos` teve quando
 * tratava bandeira pendente como bandeira desligada.
 *
 * `carregando` é exposto pelo mesmo motivo: "ainda não sei" não é "não é
 * anunciante". Quem decide painel precisa esperar; quem só desenha um pedaço de
 * tela pode seguir com o default fechado.
 *
 * Anunciante NÃO é um papel do sistema. O papel `partner` existe no enum e o
 * banco o recusa, por um gatilho de hierarquia de 20/04. Ser anunciante é ter
 * uma linha em `service_partners` ligada à conta, com status `active` — e desde
 * a migration 20260908000006 há no máximo uma por conta.
 */
export interface PartnerStatus {
  /** Tem cadastro de anunciante APROVADO. */
  isPartner: boolean;
  /** Tem cadastro, em qualquer estado — inclusive em análise ou recusado. */
  temCadastro: boolean;
  /** Quantos imóveis a conta gerencia. Zero significa "não usa o GIFI para gerir". */
  imoveis: number;
  carregando: boolean;
}

export function usePartnerStatus(): PartnerStatus {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["partner-status", user?.id],
    staleTime: 5 * 60 * 1000,
    enabled: !!user,
    queryFn: async () => {
      const [parceiro, imoveis] = await Promise.all([
        supabase
          .from("service_partners")
          .select("status")
          .eq("user_id", user!.id)
          .maybeSingle(),
        supabase
          .from("imoveis")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user!.id),
      ]);

      // Uma consulta que ERROU não pode virar "não é anunciante": seria tirar o
      // painel de quem o tem, por causa de uma falha de rede. Falhar alto aqui
      // deixa o React Query manter o último valor bom e tentar de novo.
      if (parceiro.error) throw parceiro.error;
      if (imoveis.error) throw imoveis.error;

      return {
        status: parceiro.data?.status ?? null,
        imoveis: imoveis.count ?? 0,
      };
    },
  });

  return {
    isPartner: data?.status === "active",
    temCadastro: !!data?.status,
    imoveis: data?.imoveis ?? 0,
    carregando: !!user && isLoading,
  };
}
